import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent } from 'react'

/** A finger drifts a little while holding still; more than this is a scroll or a drag. */
const MOVE_TOLERANCE_PX = 8

/**
 * Handlers that call `onLongPress` after the pointer is held still for `ms`.
 *
 * The click that ends a long press is swallowed, so holding a link opens edit
 * mode instead of also following the link.
 */
export function useLongPress(onLongPress: () => void, ms = 500) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const origin = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    origin.current = null
  }, [])

  useEffect(() => cancel, [cancel])

  return {
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      fired.current = false
      origin.current = { x: e.clientX, y: e.clientY }
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        timer.current = null
        fired.current = true
        // Clear the flag once the press ends, on the window because the press
        // usually unmounts the tile (edit mode opens) and its own click never
        // comes. A stale flag would swallow the next keyboard Enter on a tile.
        // The ending click is dispatched before the timer runs, so it is still
        // swallowed when the tile does stay.
        window.addEventListener(
          'pointerup',
          () =>
            setTimeout(() => {
              fired.current = false
            }, 0),
          { once: true },
        )
        onLongPress()
      }, ms)
    },
    onPointerMove: (e: PointerEvent) => {
      const start = origin.current
      if (!start) return
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > MOVE_TOLERANCE_PX) cancel()
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onClickCapture: (e: MouseEvent) => {
      if (!fired.current) return
      fired.current = false
      e.preventDefault()
      e.stopPropagation()
    },
    onContextMenu: (e: MouseEvent) => {
      // Touch browsers open a context menu on a held link.
      if (timer.current || fired.current) e.preventDefault()
    },
  }
}
